import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  faBan,
  faArrowUpRightFromSquare,
  faClockRotateLeft,
  faClose,
  faChevronLeft,
  faChevronRight,
  faFileCode,
  faFilterCircleXmark,
  faMagnifyingGlass,
  faRotate,
  faPrint,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import { EFiscalDocumentStatus } from "../../../../Objects/FiscalIntegration/EFiscalDocumentStatus"
import { type IFiscalDocument } from "../../../../Objects/FiscalIntegration/IFiscalDocument"
import { getFiscalDocumentStatusTranslationKey } from "../../../../Objects/FiscalIntegration/fiscalDocumentStatus"
import { type IFiscalDocumentFilter } from "../../../../Objects/FiscalIntegration/IFiscalDocumentFilter"

type Props = {
  isOpen: boolean
  documents: IFiscalDocument[]
  pageNumber: number
  pageSize: number
  totalCount: number
  hasNextPage: boolean
  onClose: () => void
  onSearch: (filter: IFiscalDocumentFilter) => Promise<void>
  onRefresh: (documentId: number) => void
  onRetry: (documentId: number) => void
  onOpenDanfce: (document: IFiscalDocument) => void
  onDownloadXml: (document: IFiscalDocument) => void
  onPrint: (document: IFiscalDocument) => void
  onCancel: (document: IFiscalDocument) => void
}

const statusClass: Record<EFiscalDocumentStatus, string> = {
  [EFiscalDocumentStatus.Pending]: "border-amber-200 bg-amber-50 text-amber-800",
  [EFiscalDocumentStatus.Processing]: "border-sky-200 bg-sky-50 text-sky-800",
  [EFiscalDocumentStatus.Authorized]: "border-emerald-200 bg-emerald-50 text-emerald-800",
  [EFiscalDocumentStatus.Rejected]: "border-red-200 bg-red-50 text-red-800",
  [EFiscalDocumentStatus.Contingency]: "border-orange-200 bg-orange-50 text-orange-800",
  [EFiscalDocumentStatus.Canceled]: "border-slate-300 bg-slate-100 text-slate-700",
  [EFiscalDocumentStatus.CancellationPending]: "border-violet-200 bg-violet-50 text-violet-800",
}

const DOCUMENTS_PAGE_SIZE = 20

const fiscalDocumentStatuses: EFiscalDocumentStatus[] = [
  EFiscalDocumentStatus.Pending,
  EFiscalDocumentStatus.Processing,
  EFiscalDocumentStatus.Authorized,
  EFiscalDocumentStatus.Rejected,
  EFiscalDocumentStatus.Contingency,
  EFiscalDocumentStatus.Canceled,
  EFiscalDocumentStatus.CancellationPending,
]

const createInitialFilter = (): IFiscalDocumentFilter => ({
  searchTerm: "",
  status: null,
  dateStart: "",
  dateEnd: "",
  pageNumber: 1,
  pageSize: DOCUMENTS_PAGE_SIZE,
})

export const FiscalDocumentsPopup: React.FC<Props> = ({
  isOpen,
  documents,
  pageNumber,
  pageSize,
  totalCount,
  hasNextPage,
  onClose,
  onSearch,
  onRefresh,
  onRetry,
  onOpenDanfce,
  onDownloadXml,
  onPrint,
  onCancel,
}) => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const [searchTerm, setSearchTerm] = useState("")
  
  const [status, setStatus] = useState("")
  
  const [dateStart, setDateStart] = useState("")
  
  const [dateEnd, setDateEnd] = useState("")
  
  const [isSearching, setIsSearching] = useState(false)
  
  const onSearchRef = useRef(onSearch)

  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setSearchTerm("")
    
    setStatus("")
    
    setDateStart("")
    
    setDateEnd("")
    
    setIsSearching(true)

    void onSearchRef
      .current(createInitialFilter())
      .catch(() => undefined)
      .finally(() => setIsSearching(false))
  }, [isOpen])

  const statusOptions = useMemo(
    () => [
      { label: t("documents.allStatuses"), value: "" },
      ...fiscalDocumentStatuses.map((value) => ({
        label: t(getFiscalDocumentStatusTranslationKey(value)),
        value: value.toString(),
      })),
    ],
    [t],
  )

  const executeSearch = async (requestedPage: number) => {
    if (isSearching) {
      return
    }

    setIsSearching(true)

    try {
      await onSearch({
        searchTerm,
        status: status === "" ? null : (Number(status) as EFiscalDocumentStatus),
        dateStart,
        dateEnd,
        pageNumber: requestedPage,
        pageSize: DOCUMENTS_PAGE_SIZE,
      })
    } catch {
      return
    } finally {
      setIsSearching(false)
    }
  }

  const clearFilters = async () => {
    if (isSearching) {
      return
    }

    setSearchTerm("")
    
    setStatus("")
    
    setDateStart("")
    
    setDateEnd("")
    
    setIsSearching(true)

    try {
      await onSearch(createInitialFilter())
    } catch {
      return
    } finally {
      setIsSearching(false)
    }
  }

  const firstVisibleDocument = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  
  const lastVisibleDocument = Math.min(pageNumber * pageSize, totalCount)
  
  const hasActiveFilters = Boolean(searchTerm || status || dateStart || dateEnd)

  if (!isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              icon={faClockRotateLeft}
              iconColor={THEME.colors.iconBGColor}
              title={t("documents.title")}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.myrestaurant.fiscalintegration.documents.1"
                  title={t("helpPopup:global.fiscalDocuments.title")}
                  description={t("helpPopup:global.fiscalDocuments.description")}
                  steps={t("helpPopup:global.fiscalDocuments.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalDocuments.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="flex max-h-[76vh] min-h-[420px] flex-col p-3">
              <div className="mb-3 flex flex-col gap-3 rounded-xl border-2 border-indigo-100 bg-indigo-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <strong className="text-sm text-indigo-950">{t("documents.summary", { count: totalCount })}</strong>

                  <p className="mt-0.5 text-xs text-indigo-800">{t("documents.description")}</p>
                </div>

                <button
                  type="button"
                  disabled={isSearching}
                  onClick={() => void executeSearch(pageNumber)}
                  className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.onReload"
                >
                  <FontAwesomeIcon icon={faRotate} />
                  {t("actions.refresh")}
                </button>
              </div>

              <form
                className="mb-3 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 lg:grid-cols-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  void executeSearch(1)
                }}
              >
                <InputField
                  type="text"
                  icon={faMagnifyingGlass}
                  value={searchTerm}
                  maxLength={100}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t("documents.searchPlaceholder")}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.input.search"
                />

                <GenericDropDown
                  data={statusOptions}
                  initialState={status}
                  handlerFuncion={(event) => setStatus(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.select.status"
                />

                <InputField
                  type="date"
                  value={dateStart}
                  max={dateEnd || undefined}
                  onChange={(event) => setDateStart(event.target.value)}
                  placeholder={t("documents.dateStart")}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.input.dateStart"
                />

                <InputField
                  type="date"
                  value={dateEnd}
                  min={dateStart || undefined}
                  onChange={(event) => setDateEnd(event.target.value)}
                  placeholder={t("documents.dateEnd")}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.input.dateEnd"
                />

                <div className="flex flex-wrap gap-2 lg:col-span-2 lg:justify-end">
                  <button
                    type="button"
                    disabled={isSearching || !hasActiveFilters}
                    onClick={() => void clearFilters()}
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.clearFilters"
                  >
                    <FontAwesomeIcon icon={faFilterCircleXmark} />
                    {t("documents.clearFilters")}
                  </button>

                  <button
                    type="submit"
                    disabled={isSearching}
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.search"
                  >
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    {isSearching ? t("documents.searching") : t("documents.search")}
                  </button>
                </div>
              </form>

              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1">
                {documents.length === 0 && (
                  <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="mb-3 text-2xl text-slate-400" />
                    {t(hasActiveFilters ? "documents.emptySearch" : "documents.empty")}
                  </div>
                )}

                {documents.map((document) => (
                  <article key={document.id} className="rounded-xl border-2 border-slate-100 bg-white p-3 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <strong className="text-sm text-slate-900">
                          {t("documents.order", { id: document.orderEntityId })}
                        </strong>

                        <p className="text-[11px] text-slate-500">
                          {t("documents.number", {
                            number: document.number ?? t("documents.waitingNumber"),
                            series: document.series ?? "-",
                          })}
                        </p>
                      </div>

                      <span
                        className={`inline-flex w-fit items-center rounded-full border px-2.5 py-1 text-[10px] font-black ${statusClass[document.status]}`}
                      >
                        {t(getFiscalDocumentStatusTranslationKey(document.status))}
                      </span>
                    </div>

                    {(document.message || document.cancellationError) && (
                      <div
                        className={`mt-2 rounded-lg border p-3 text-xs leading-relaxed ${
                          document.status === EFiscalDocumentStatus.Rejected || document.cancellationError
                            ? "border-red-200 bg-red-50 text-red-900"
                            : "border-slate-200 bg-slate-50 text-slate-700"
                        }`}
                        role={
                          document.status === EFiscalDocumentStatus.Rejected || document.cancellationError
                            ? "alert"
                            : undefined
                        }
                      >
                        {(document.status === EFiscalDocumentStatus.Rejected || document.cancellationError) && (
                          <strong className="mb-1 flex items-center gap-2">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            {t(
                              document.cancellationError
                                ? "documents.cancellationFailureReason"
                                : "documents.failureReason",
                            )}
                          </strong>
                        )}

                        <span>{document.cancellationError ?? document.message}</span>
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => onRefresh(document.id)}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100"
                        title={t("documents.refresh")}
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.refresh"
                      >
                        <FontAwesomeIcon icon={faRotate} />
                        {t("documents.refresh")}
                      </button>

                      {document.status === EFiscalDocumentStatus.Rejected && !document.cancellationError && (
                        <button
                          type="button"
                          onClick={() => onRetry(document.id)}
                          className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100"
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.onRetryDocumentId"
                        >
                          <FontAwesomeIcon icon={faClockRotateLeft} />
                          {t("documents.retry")}
                        </button>
                      )}

                      {document.hasDanfce && document.danfceUrl && (
                        <button
                          type="button"
                          onClick={() => onOpenDanfce(document)}
                          className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs font-bold text-sky-800 hover:bg-sky-100"
                          title={t("documents.openDanfce")}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.openDanfce"
                        >
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          {t("documents.fiscalReceipt")}
                        </button>
                      )}

                      {document.canPrint && (
                        <button
                          type="button"
                          onClick={() => onPrint(document)}
                          className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100"
                          title={t("documents.printDanfce")}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.printDanfce"
                        >
                          <FontAwesomeIcon icon={faPrint} />
                          {t("documents.print")}
                        </button>
                      )}

                      {document.hasXml && (
                        <button
                          type="button"
                          onClick={() => onDownloadXml(document)}
                          className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-bold text-indigo-800 hover:bg-indigo-100"
                          title={t("documents.downloadXml")}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.downloadXml"
                        >
                          <FontAwesomeIcon icon={faFileCode} />
                          {t("documents.xml")}
                        </button>
                      )}

                      {document.status === EFiscalDocumentStatus.Authorized && document.canCancel !== false && (
                        <button
                          type="button"
                          onClick={() => onCancel(document)}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100"
                          title={t("documents.cancel")}
                          data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.cancel"
                        >
                          <FontAwesomeIcon icon={faBan} />
                          {t("documents.cancel")}
                        </button>
                      )}
                    </div>

                    {document.status === EFiscalDocumentStatus.Authorized &&
                      document.canCancel === false &&
                      !document.cancellationError && (
                        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900">
                          {t("documents.cancellationDeadlineExceeded")}
                        </p>
                      )}
                  </article>
                ))}
              </div>

              <div className="mt-3 flex flex-col gap-2 border-t border-slate-200 pt-3 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {t("documents.paginationSummary", {
                    first: firstVisibleDocument,
                    last: lastVisibleDocument,
                    total: totalCount,
                  })}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={isSearching || pageNumber <= 1}
                    onClick={() => void executeSearch(pageNumber - 1)}
                    className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-bold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.previousPage"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    {t("documents.previousPage")}
                  </button>

                  <span className="font-bold text-slate-700">{t("documents.page", { page: pageNumber })}</span>

                  <button
                    type="button"
                    disabled={isSearching || !hasNextPage}
                    onClick={() => void executeSearch(pageNumber + 1)}
                    className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-bold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalDocumentsPopup.button.nextPage"
                  >
                    {t("documents.nextPage")}
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
